import { ReactNode, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowForward } from '@mui/icons-material'
import { Box, Chip, Typography } from '@mui/material'
import { routes } from '@/router'
import { UserProfile } from '@/types'
import { SessionField, SessionScreen } from '../session-screen'
import { POPULAR_AREAS, PRACTICE_AREAS } from './profile.constants'
import { flowProgress, isMedicine, nextProfileStep } from './profile-steps'
import { ProfileStepFrame } from './profile-step-frame'
import { saveProfile } from './save-profile'

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function highlight(area: string, query: string) {
  const index = normalize(area).indexOf(normalize(query))
  if (index < 0) return area

  return (
    <>
      {area.slice(0, index)}
      <Box
        component="mark"
        sx={{
          bgcolor: 'transparent',
          color: 'text.secondary',
          fontWeight: 700,
        }}
      >
        {area.slice(index, index + query.length)}
      </Box>
      {area.slice(index + query.length)}
    </>
  )
}

export function ProfileAreaPage() {
  return (
    <ProfileStepFrame step="area">
      {(user) => <AreaStep user={user} />}
    </ProfileStepFrame>
  )
}

function AreaStep({ user }: { user: UserProfile }) {
  const navigate = useNavigate()
  const location = useLocation()
  const accountCreated = Boolean(
    (location.state as { accountCreated?: boolean } | null)?.accountCreated,
  )
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)

  const normalizedQuery = query.trim()
  const hits = normalizedQuery
    ? PRACTICE_AREAS.filter((area) =>
        normalize(area).includes(normalize(normalizedQuery)),
      )
    : []
  const exact = hits.some(
    (area) => normalize(area) === normalize(normalizedQuery),
  )

  async function pick(area: string) {
    if (saving) return
    setSaving(true)

    try {
      const refreshed = await saveProfile({
        practice_area: area.trim(),
        ...(isMedicine(area) ? {} : { occupation: 'other' }),
      })
      const step = nextProfileStep(refreshed)
      navigate(step ? routes.session.profile[step] : routes.home)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SessionScreen
      title="Qual é a sua área de atuação?"
      subtitle={
        accountCreated
          ? `Conta criada, ${user.first_name || 'olá'}! Agora vamos conhecer você para mostrar os produtos certos.`
          : 'Vamos mostrar os produtos certos para você.'
      }
      progress={flowProgress('area', user)}
      canBack={false}
    >
      <SessionField
        id="practice-area"
        label="Buscar área"
        value={query}
        placeholder="Digite o nome da área"
        autoComplete="off"
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />

      {!normalizedQuery ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Typography variant="body2" color="text.secondary">
            Mais procurados
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {POPULAR_AREAS.map((area) => (
              <Chip
                key={area}
                label={area}
                variant="outlined"
                clickable
                disabled={saving}
                onClick={() => pick(area)}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          {hits.map((area) => (
            <AreaResult key={area} disabled={saving} onClick={() => pick(area)}>
              {highlight(area, normalizedQuery)}
            </AreaResult>
          ))}
          {!exact && (
            <AreaResult disabled={saving} onClick={() => pick(normalizedQuery)}>
              <Box component="span" color="text.secondary">
                Usar “{normalizedQuery}”
              </Box>
            </AreaResult>
          )}
        </Box>
      )}
    </SessionScreen>
  )
}

function AreaResult({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode
  onClick: () => void
  disabled: boolean
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        textAlign: 'left',
        border: 0,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 2,
        py: 1.75,
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
        '&:last-child': { borderBottom: 0 },
        '&:hover': { bgcolor: 'primary.50' },
      }}
    >
      <span>{children}</span>
      <ArrowForward fontSize="small" />
    </Box>
  )
}
